/**
 * @file units.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-18
 * @modified 2026-10-12
 *
 * What an item's quantity is counted in — the client's side of the rule that
 * lives in utils/units.js and utils/tonnage.js on the server.
 *
 * `inventory.items.unit_weight_kg` is the weight of ONE quantity unit, and
 * tonnage is quantity × unit_weight_kg / 1000. A quantity on its own is
 * ambiguous: 40 of a bagged product is forty bags, 40 of a bulk one is forty
 * tonnes. The number on screen never said which, which is what these helpers
 * are for.
 *
 * Written "25kg bag" — no space between the number and the unit — as Cam asked.
 * Tonnes are a capital T, per the project rule.
 *
 * The server's copy is deliberately server-only for PO paperwork (one rule, one
 * place, worked out before it reaches the client). This is the display side of
 * the same rule for the item modals, which read an item row directly.
 */

import { formatCount } from './numberFormatters';

/**
 * How an item is handled on a lorry. The weight follows the choice — it is not
 * typed — and it is what transport planning turns quantities into tonnes with.
 * Shared with InventoryItemFormModal, which offers exactly this list.
 */
export const UNIT_OPTIONS = [
  { value: '1000', label: 'Bulk — per tonne' },
  { value: '500', label: 'Tote (500kg)' },
  { value: '25', label: '25kg bag' },
  { value: '20', label: '20kg bag' },
  { value: 'other', label: 'Other — by the pallet' }
];

/**
 * "Other": anything not bulk, a tote or a 20/25kg bag, described by how it
 * arrives — so many to a pallet, a pallet weighing so much. The unit weight is
 * then pallet weight / qty per pallet (the server works it out again on save,
 * and that is what everything downstream reads).
 */
export const OTHER_UNIT = 'other';

/**
 * A saved LINE's units, back into the grid's shape. A line that stored both a
 * unit weight and a qty per pallet, on a weight none of the fixed units has,
 * was set by the pallet ("Other") — reopened that way, pallet weight and all.
 * @param {{unit_weight_kg, qty_per_pallet, pallets}} saved  blank/null = the item's
 */
export function lineUnitsFromSaved({ unit_weight_kg, qty_per_pallet, pallets }) {
  // None — no weight (a stored 0, utils/lineUnits): no pallets either.
  const blankKg = unit_weight_kg === '' || unit_weight_kg === null || unit_weight_kg === undefined;
  if (!blankKg && Number(unit_weight_kg) === 0) {
    return { unit_weight_kg: '0', qty_per_pallet: '', pallets: '0', unit_is_other: false, pallet_weight_kg: '' };
  }
  const kg = parseFloat(unit_weight_kg);
  const per = parseFloat(qty_per_pallet);
  const fixed = UNIT_OPTIONS.some(o => o.value !== OTHER_UNIT && Number(o.value) === kg);
  const isOther = kg > 0 && per > 0 && !fixed;
  return {
    unit_weight_kg: kg > 0 ? String(kg) : '',
    qty_per_pallet: per > 0 ? String(per) : '',
    pallets: parseFloat(pallets) >= 0 && pallets !== null && pallets !== '' ? String(parseFloat(pallets)) : '',
    unit_is_other: isOther,
    pallet_weight_kg: isOther ? String(Math.round(kg * per * 100) / 100) : ''
  };
}

/**
 * Pallets for a quantity: the typed count if there is one, else quantity / qty
 * per pallet, else null (unknown — not zero, which would read as "none").
 * The same rule as palletsFor in the server's utils/units.js, which the PO-H
 * prints from.
 */
export function palletsFor(quantity, perPallet, typed) {
  const t = parseFloat(typed);
  if (Number.isFinite(t) && t >= 0 && typed !== null && typed !== '') return Math.round(t * 100) / 100;
  const per = parseFloat(perPallet);
  const qty = parseFloat(quantity);
  return per > 0 && Number.isFinite(qty) ? Math.round((qty / per) * 100) / 100 : null;
}

/** Pallet weight / qty per pallet, or null when either is missing. */
export const unitWeightFromPallet = (palletWeight, perPallet) => {
  const pw = parseFloat(palletWeight);
  const per = parseFloat(perPallet);
  return pw > 0 && per > 0 ? pw / per : null;
};

/**
 * Sales ledger codes whose quantity is denominated in tonnes even with no
 * weight set. Mirrors BULK_SALES_ACCOUNT_CODES in the server's utils/tonnage.js
 * — change both together.
 */
export const BULK_SALES_ACCOUNT_CODES = ['201', '202', '204', '206', '209'];

/** unit_weight_kg arrives as a number, a numeric string ("25.00") or blank. */
export const unitValueFor = (weight) => {
  if (weight === '' || weight == null) return '';
  const n = Number(weight);
  return Number.isFinite(n) ? String(n) : '';
};

/** An item saved with a weight outside the list keeps it, shown for what it is. */
export const unitOptionsFor = (weight) => {
  const value = unitValueFor(weight);
  return value && value !== OTHER_UNIT && !UNIT_OPTIONS.some(o => o.value === value)
    ? [...UNIT_OPTIONS, { value, label: `${Number(value).toLocaleString('en-GB')}kg` }]
    : UNIT_OPTIONS;
};

/** The weight as a positive number, or null when it is unset or unusable. */
export const weightKgOf = (weight) => {
  const kg = Number(unitValueFor(weight));
  return Number.isFinite(kg) && kg > 0 ? kg : null;
};

const NAMED = { 1000: 'T', 500: '500kg tote', 25: '25kg bag', 20: '20kg bag' };

/**
 * "T", "25kg bag", "500kg tote", "12.5kg" — or '' when the weight is unknown,
 * because a guessed unit beside a quantity is worse than none.
 */
export function unitLabelFor(weight) {
  const kg = weightKgOf(weight);
  if (kg === null) return '';
  return NAMED[kg] || `${Number(kg.toFixed(3))}kg`;
}

/**
 * How the unit sits beside a quantity: "T" for tonnes, which reads as a unit of
 * measure ("12.5 T"), and "× 25kg bag" for packs ("40 × 25kg bag"), which reads
 * as a count of them.
 */
export function unitSuffixFor(weight) {
  const label = unitLabelFor(weight);
  if (!label) return '';
  return label === 'T' ? 'T' : `× ${label}`;
}

/** The dropdown's own wording, for showing a saved choice back read-only. */
export function unitChoiceLabel(weight, palletWeight = null) {
  if (parseFloat(palletWeight) > 0) return 'Other — by the pallet';
  const value = unitValueFor(weight);
  if (!value) return '';
  const option = unitOptionsFor(weight).find(o => o.value === value);
  return option ? option.label : `${Number(value).toLocaleString('en-GB')}kg`;
}

export const isBulkSalesCode = (code) =>
  BULK_SALES_ACCOUNT_CODES.includes(String(code || '').toUpperCase());

const tidy = (n) => Number(n.toFixed(2)).toLocaleString('en-GB');

/**
 * What a quantity of this item means, in the words a user needs: the unit, the
 * weight behind it, and a sentence saying how the tonnage follows. The blank
 * cases are the ones worth spelling out — a blank weight on a bulk sales code
 * still counts as tonnes, and a blank weight anywhere else counts as nothing at
 * all, which is deliberate for shop stock and a mistake on anything delivered.
 *
 * @param {object} item  an inventory.items row (unit_weight_kg, sales_account_code)
 * @returns {{unit: string, perUnit: string, means: string, isSet: boolean, countsInTonnage: boolean}}
 */
/**
 * How many of an item's quantity units make a pallet, formatted, or '' when
 * nothing is recorded.
 *
 * `inventory.items.qty_per_pallet` is local-only and NULL on most items, and a
 * blank one is shown nowhere: it is a stacking detail nobody has needed rather
 * than a gap worth flagging (contrast unit_weight_kg, whose absence is always
 * surfaced because it silently drops a line out of the tonnage reports).
 *
 * A count of packs, so only the places it has (formatCount — Cam,
 * 2026-10-08): "48", "1.2", "12.125" — numeric columns arrive as strings from
 * pg ("48.0000").
 */
export function qtyPerPalletLabel(item) {
  const raw = item?.qty_per_pallet;
  if (raw === null || raw === undefined || raw === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return '';
  return formatCount(n);
}

export function quantityMeansFor(item) {
  const kg = weightKgOf(item?.unit_weight_kg);
  const code = String(item?.sales_account_code || '').toUpperCase();

  if (kg === null) {
    const bulk = isBulkSalesCode(code);
    return {
      unit: 'Not set',
      perUnit: '—',
      means: bulk
        ? `No weight set, but sales account ${code} is bulk — a quantity of 1 counts as 1 T.`
        : 'No weight set, so a quantity here adds nothing to tonnage. Set a unit if it travels on a lorry.',
      isSet: false,
      countsInTonnage: bulk
    };
  }

  return {
    unit: unitChoiceLabel(kg, item?.pallet_weight_kg),
    perUnit: `${tidy(kg)}kg`,
    // "one 25kg bag" reads naturally; "one 22.5kg" does not, so an item on an
    // odd weight is described by the weight itself.
    means: kg === 1000
      ? 'A quantity of 1 is 1 T.'
      : `A quantity of 1 is ${NAMED[kg] ? `one ${NAMED[kg]}` : `${tidy(kg)}kg`} — ${tidy(1000 / kg)} makes 1 T.`,
    isSet: true,
    countsInTonnage: true
  };
}
